// Minimal, dependency-free markdown renderer tuned to our seeded blog posts.
// Handles: ## H2, ### H3, paragraphs, - bullet lists, 1. numbered lists,
// **bold**, *italic*, [text](url) links.
import { Fragment, type ReactNode } from "react";

function renderInline(text: string): ReactNode[] {
  // Split by markdown tokens while preserving them.
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);
  return tokens.map((tok, i) => {
    if (!tok) return <Fragment key={i} />;
    if (tok.startsWith("**") && tok.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-foreground">
          {tok.slice(2, -2)}
        </strong>
      );
    }
    if (tok.startsWith("*") && tok.endsWith("*")) {
      return <em key={i}>{tok.slice(1, -1)}</em>;
    }
    const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const url = link[2].trim();
      const isSafeUrl = /^https?:\/\//i.test(url) || url.startsWith("/") || url.startsWith("#") || url.startsWith("mailto:") || url.startsWith("tel:");
      if (!isSafeUrl) {
        return <Fragment key={i}>{link[1]}</Fragment>;
      }
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-mint underline underline-offset-4 hover:text-mint/80"
        >
          {link[1]}
        </a>
      );
    }
    return <Fragment key={i}>{tok}</Fragment>;
  });
}

type Block =
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

function parse(body: string): Block[] {
  const lines = body.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      i++;
      continue;
    }
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    // paragraph (may span multiple non-blank lines)
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("## ") &&
      !lines[i].trim().startsWith("### ") &&
      !lines[i].trim().startsWith("- ") &&
      !/^\d+\.\s/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: "p", text: paraLines.join(" ") });
  }
  return blocks;
}

export function ArticleBody({ body }: { body: string }) {
  const blocks = parse(body);
  return (
    <div className="prose-fenny">
      {blocks.map((b, idx) => {
        if (b.type === "h2") {
          return (
            <h2 key={idx} className="mt-14 text-3xl font-semibold tracking-tight md:text-4xl">
              {b.text}
            </h2>
          );
        }
        if (b.type === "h3") {
          return (
            <h3 key={idx} className="mt-10 text-xl font-semibold tracking-tight md:text-2xl">
              {b.text}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={idx} className="mt-5 space-y-2 pl-5 text-base leading-relaxed text-foreground/85 md:text-[17px]">
              {b.items.map((it, j) => (
                <li key={j} className="relative pl-3 marker:text-mint before:absolute before:left-0 before:top-3 before:h-1.5 before:w-1.5 before:rounded-full before:bg-mint">
                  {renderInline(it)}
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={idx} className="mt-5 list-decimal space-y-2 pl-6 text-base leading-relaxed text-foreground/85 marker:text-mint md:text-[17px]">
              {b.items.map((it, j) => (
                <li key={j} className="pl-1">{renderInline(it)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p
            key={idx}
            className="mt-5 text-base leading-[1.75] text-foreground/85 md:text-[17px]"
          >
            {renderInline(b.text)}
          </p>
        );
      })}
    </div>
  );
}
